const shiClaimBase = "https://one.shi.com/claim-type/";
export const claimTypes = {
  accountGroupId: `${shiClaimBase}account-group-id`,
  accountId: `${shiClaimBase}account-id`,
  accountName: `${shiClaimBase}account-name`,
  axNumber: `${shiClaimBase}ax-number`,
  primaryAxNumber: `${shiClaimBase}ax-number-primary`,
  compositeCatalogId: `${shiClaimBase}composite-catalog-id`,
  contactId: `${shiClaimBase}contact-id`,
  firstName: `${shiClaimBase}first-name`,
  hasShiClaims: `${shiClaimBase}has-shi-one-claims`,
  impersonatorContactId: `${shiClaimBase}impersonator-contact-id`,
  impersonatorEmail: `${shiClaimBase}impersonator-email`,
  isCustomer: `${shiClaimBase}is-customer`,
  isImpersonating: `${shiClaimBase}is-impersonating`,
  isShi: `${shiClaimBase}is-shi`,
  isShared: `${shiClaimBase}is-shared`,
  isVirtualAdmin: `${shiClaimBase}is-virtual-admin`,
  lastName: `${shiClaimBase}last-name`,
  permission: `${shiClaimBase}permission`,
  roles: `${shiClaimBase}role-types`,
  userEmail: `${shiClaimBase}user-profile-contact-email`,
  isGuestUser: `${shiClaimBase}is-guest-user`,
  orgUnitId: `${shiClaimBase}org-unit-id`,
  orgUnitIdCurrent: `${shiClaimBase}org-unit-id-current`,
  orgUnitName: `${shiClaimBase}org-unit-name`,
  shiComLoginId: `${shiClaimBase}shi-com-login-id`,
  shiComEmployeeId: `${shiClaimBase}shi-com-employee-id`,
  shiComPersonalizationCode: `${shiClaimBase}shi-com-personalization-code`,
  sitePrefix: `${shiClaimBase}site-prefix`,
  tenantId: `${shiClaimBase}tenant-id`,
  tenantName: `${shiClaimBase}tenant-name`,
  tenantCrmAccountId: `${shiClaimBase}tenant-crm-account-id`,
  permissionResource: `${shiClaimBase}permission-resource`,
  userId: `${shiClaimBase}user-id`,
  userEntraId: `${shiClaimBase}user-entra-id`,
  crmAccountId: `${shiClaimBase}crm-account-id`,
  crmReportingBuPath: `${shiClaimBase}crm-reporting-bu-path`,
  primaryAeEntraId: `${shiClaimBase}primary-ae-entra-id`,
  primaryAeReportingBuId: `${shiClaimBase}primary-ae-reporting-bu-id`,
  primaryAeReportingBuPath: `${shiClaimBase}primary-ae-reporting-bu-path`,
  salesKey: `${shiClaimBase}sales-key`,
  salesOrgStatus: `${shiClaimBase}sales-org-status`,
};
export type ClaimTypes = keyof typeof claimTypes;

export function hasMultipleTenantAccounts(
  claims: readonly { claimType?: string | undefined }[],
): boolean {
  return (
    claims.filter((claim) => claim.claimType === claimTypes.tenantCrmAccountId)
      .length > 1
  );
}

export const roleTypes = {
  None: 0,
  CustomerUser: 1,
  CustomerAdmin: 2,
  ShiUser: 4,
  ShiAdmin: 8,
  ShiResource: 16,
  ShiImpersonateUser: 32,
  ShiImpersonateVirtualAdmin: 64,
  ProcurementAdmin: 128,
  SecurityAdmin: 256,
  GlobalAdmin: 512,
  ShiOnly: 4 | 8 | 16,
};
export type RoleTypes = keyof typeof roleTypes;

export const permissionResourceTypes = {
  "service/contract": "service/contract",
  "service/invoice": "service/invoice",
  "supportcenter/project": "supportcenter/project",
  "cloud/aws/account": "cloud/aws/account",
  "cloud/aws/report": "cloud/aws/report",
  "cloud/aws/monitoring": "cloud/aws/monitoring",
  "cloud/aws/blueprints": "cloud/aws/blueprints",
  "cloud/aws/recommendations": "cloud/aws/recommendations",
  "cloud/aws/compliance": "cloud/aws/compliance",
  "cloud/azure/subscription": "cloud/azure/subscription",
  "cloud/azure/report": "cloud/azure/report",
  "cloud/azure/blueprints": "cloud/azure/blueprints",
  "cloud/azure/monitoring": "cloud/azure/monitoring",
  "cloud/azure/backup": "cloud/azure/backup",
  "cloud/azure/recommendations": "cloud/azure/recommendations",
  "cloud/azure/compliance": "cloud/azure/compliance",
  "cloud/o365/subscription": "cloud/o365/subscription",
  "cloud/gcp/account": "cloud/gcp/account",
  "cloud/gcp/report": "cloud/gcp/report",
  "cloud/gcp/project": "cloud/gcp/project",
  "cloud/datadog/report": "cloud/datadog/report",
  "assets/warehouse": "assets/warehouse",
  "assets/integrationCenter/reports": "assets/integrationCenter/reports",
  "assets/managed": "assets/managed",
  "assets/estate": "assets/estate",
  "cisco/reports": "cisco/reports",
  "dashboards/multicloudConsumption": "dashboards/multicloudConsumption",
};
export type PermissionResourceTypes = keyof typeof permissionResourceTypes;

export const permissionActionTypes = {
  "contract:ReadContracts": "contract:ReadContracts",
  "invoice:ReadInvoices": "invoice:ReadInvoices",
  "project:ReadProjects": "project:ReadProjects",
  "account:ReadAccounts": "account:ReadAccounts",
  "account:CreateAccounts": "account:CreateAccounts",
  "report:ReadReports": "report:ReadReports",
  "alert:ReadAlerts": "alert:ReadAlerts",
  "alert:DeployBlueprints": "alert:DeployBlueprints",
  "recommendations:ReadRecommendations": "recommendations:ReadRecommendations",
  "compliance:ReadCompliance": "compliance:ReadCompliance",
  "backup:ReadAlerts": "backup:ReadAlerts",
  "backup:ReadBackup": "backup:ReadBackup",
  "assets:ReadAssetEstate": "assets:ReadAssetEstate",
  "assets:ReadOrderData": "assets:ReadOrderData",
  "assets:ManageOrderData": "assets:ManageOrderData",
  "assets:ReadManagedDevices": "assets:ReadManagedDevices",
  "assets:ReadWarehouseInventory": "assets:ReadWarehouseInventory",
  "assets:ReadWarehouseOrders": "assets:ReadWarehouseOrders",
};
export type PermissionActionTypes = keyof typeof permissionActionTypes;

export const enum ShiOnePermission {
  aiReadinessRead = "ai-readiness-read",
  assetEstateRead = "asset-estate-read",
  assetOrderDataRead = "asset-order-data-read",
  assetOrderDataManage = "asset-order-data-manage",
  awsAccountCreate = "aws-account-create",
  awsAccountRead = "aws-account-read",
  awsBlueprintDeploy = "aws-blueprint-deploy",
  awsComplianceRead = "aws-compliance-read",
  awsConsumptionRead = "aws-consumption-read",
  awsCostAllocationTagRead = "aws-cost-allocation-tag-read",
  awsInvoiceRead = "aws-invoice-read",
  awsMonitoringAlertRead = "aws-monitoring-alert-read",
  awsRecommendationRead = "aws-recommendation-read",
  azureAccountCreate = "azure-account-create",
  azureAccountRead = "azure-account-read",
  azureBackupAlertRead = "azure-backup-alert-read",
  azureBlueprintDeploy = "azure-blueprint-deploy",
  azureComplianceRead = "azure-compliance-read",
  azureConsumptionRead = "azure-consumption-read",
  azureInvoiceRead = "azure-invoice-read",
  azureMonitoringAlertRead = "azure-monitoring-alert-read",
  azureRecommendationRead = "azure-recommendation-read",
  catalystRead = "catalyst-read",
  ciscoReportRead = "cisco-report-read",
  cloudSubsRead = "cloud-subs-read",
  cloudMatrixRead = "cloud-matrix-read",
  contentImageUpload = "content-image-upload",
  contentManage = "content-manage",
  contentView = "content-view",
  customerWelcomeEdit = "customer-welcome-edit",
  datadogConsumptionRead = "datadog-consumption-read",
  devOpsPostureReviewRead = "dev-ops-posture-review-read",
  featuredProductsAdd = "featured-products-add",
  featuredProductsDelete = "featured-products-delete",
  featuredProductsView = "featured-products-view",
  gcpAccountRead = "gcp-account-read",
  gcpConsumptionRead = "gcp-consumption-read",
  gcpInvoiceRead = "gcp-invoice-read",
  knowledgeBaseRead = "knowledge-base-read",
  labsRead = "labs-read",
  labsOnDemandRequest = "labs-on-demand-request",
  managedDevicesRead = "asset-managed-devices-read",
  microsoftInsightsRead = "microsoft-insights-read",
  manufacturerInsightsRead = "manufacturer-insights-read",
  manufacturerInsightsSettingsWrite = "manufacturer-insights-settings-write",
  multicloudConsumptionRead = "multicloud-consumption-read",
  o365AccountRead = "o365-account-read",
  o365BackupStatusRead = "o365-backup-status-read",
  o365InvoiceRead = "o365-invoice-read",
  orderEdit = "order-edit",
  orderRmaRequest = "order-rma-request",
  orderSearchMy = "order-search-my",
  orderSearchOrgUnit = "order-search-org-unit",
  orderView = "order-view",
  orgUnitChange = "org-unit-change",
  productAdvancedSearch = "product-advanced-search",
  productBulkSearch = "product-bulk-search",
  productCategoryBrowse = "product-category-browse",
  productFavorite = "product-favorite",
  productInteractiveFind = "product-interactive-find",
  productRecentlyViewed = "product-recently-viewed",
  productView = "product-view",
  productViewPopular = "product-view-popular",
  productViewSmartSuggestions = "product-view-smart-suggestions",
  quoteViewMy = "quote-view-my",
  quoteViewOrgUnit = "quote-view-org-unit",
  quoteViewTenant = "quote-view-tenant",
  renewalsView = "renewals-view",
  reportingRead = "reporting-read",
  reportEdit = "report-edit",
  reportRead = "report-read",
  roadmapsEdit = "roadmaps-edit",
  roadmapsManage = "roadmaps-manage",
  roadmapsRead = "roadmaps-read",
  salesTeamContactInfoView = "sales-team-contact-info-view",
  securityPostureReviewRead = "security-posture-review-read",
  serviceContractRead = "service-contract-read",
  serviceInvoiceRead = "service-invoice-read",
  serviceProjectRead = "service-project-read",
  settingsCostCenterManage = "settings-cost-center-manage",
  settingsGeneralManage = "settings-general-manage",
  settingsIntegrationManage = "settings-integration-manage",
  settingsUserManage = "settings-user-manage",
  shoppingCartQuoteRetrieve = "shopping-cart-quote-retrieve",
  shoppingCartSave = "shopping-cart-save",
  shoppingCartSaveAsQuote = "shopping-cart-save-as-quote",
  shoppingCartSend = "shopping-cart-send",
  shoppingCartView = "shopping-cart-view",
  shoppingMenuView = "shopping-menu-view",
  streetAddressVisibilityOu = "street-address-visibility-ou",
  streetAddressEmployeeEdit = "street-address-employee-edit",
  streetAddressOuAdmin = "street-address-ou-admin",
  storefrontAssignUsers = "storefront-assign-users",
  storefrontManage = "storefront-manage",
  storefrontView = "storefront-view",
  supportRequestRead = "support-request-read",
  supportRequestReadAll = "support-request-read-all",
  supportRequestSubmit = "support-request-submit",
  tpxAssessmentRead = "tpx-assessment-read",
  userSettingsUpdate = "user-settings-update",
  userSettingsView = "user-settings-view",
  warehouseInventoryRead = "warehouse-inventory-read",
  warehouseOrderRead = "warehouse-order-read",
  awsMaturityRead = "aws-maturity-read",
  itProjectsRead = "it-projects-read",
  itProjectsManage = "it-projects-manage",
  webOrderApprove = "web-order-approve",
  webOrderViewPendingOu = "web-order-view-pending-ou",
  newsCenterRead = "news-center-read",
}
